import React, { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  DollarSign,
  Calendar,
  Building,
  Download,
  Filter,
  Play,
  FileText,
  Users,
  Wallet,
  Hash,
  Loader2,
  CheckCircle,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  FileSpreadsheet,
  File,
  Database,
  GitCompare,
  Plus,
  Minus,
  RefreshCw,
  Edit3,
  Eye,
  EyeOff,
  Code,
  Upload,
  Save,
  FileUp,
  HelpCircle,
  BookOpen,
  Search,
  ClipboardCheck,
  ArrowRight,
} from 'lucide-react';
import { prefundingApi } from '../../services/api';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const PrefundingReport = () => {
  // Tab state
  const [activeTab, setActiveTab] = useState('run'); // 'run' or 'compare'

  // Common state
  const [tenantCode, setTenantCode] = useState('');
  const [runAsOf, setRunAsOf] = useState(new Date().toISOString().split('T')[0]);
  const [showFilters, setShowFilters] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const exportMenuRef = useRef(null);
  const pageSize = 50;

  // Compare state
  const [previousRunId, setPreviousRunId] = useState('');
  const [showUnchanged, setShowUnchanged] = useState(false);
  const [unchangedPage, setUnchangedPage] = useState(1);
  const [showQuery, setShowQuery] = useState(false);

  // Update Status state
  const [updateRunId, setUpdateRunId] = useState('');
  const [uploadedFileData, setUploadedFileData] = useState(null);
  const [matchedRecords, setMatchedRecords] = useState([]);
  const [updatePage, setUpdatePage] = useState(1);
  const [isUpdateSaved, setIsUpdateSaved] = useState(false);
  const [isProcessingFile, setIsProcessingFile] = useState(false);
  const fileInputRef = useRef(null);

  // Help section state
  const [showHelp, setShowHelp] = useState(false);

  // Close export menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(event.target)) {
        setShowExportMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch tenants for dropdown
  const { data: tenantsData, isLoading: tenantsLoading } = useQuery({
    queryKey: ['prefunding-tenants'],
    queryFn: async () => {
      const response = await prefundingApi.getTenants();
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
  });

  // Fetch run history for compare/update dropdown
  const { data: runHistoryData, isLoading: runHistoryLoading } = useQuery({
    queryKey: ['prefunding-run-history', tenantCode],
    queryFn: async () => {
      if (!tenantCode) return [];
      const response = await prefundingApi.getRunHistory(tenantCode);
      return response.data;
    },
    enabled: !!tenantCode && (activeTab === 'compare' || activeTab === 'update'),
    staleTime: 60 * 1000,
  });

  const tenants = tenantsData || [];
  const runHistory = runHistoryData || [];

  // Run report mutation
  const runReportMutation = useMutation({
    mutationFn: async () => {
      const response = await prefundingApi.runReport(tenantCode, runAsOf);
      return response.data;
    },
    onSuccess: () => {
      setIsSaved(false);
    },
  });

  // Compare mutation
  const compareMutation = useMutation({
    mutationFn: async () => {
      const response = await prefundingApi.runCompare(tenantCode, runAsOf, previousRunId);
      return response.data;
    },
    onSuccess: () => {
      setIsSaved(false);
    },
  });

  // Save report mutation
  const saveReportMutation = useMutation({
    mutationFn: async () => {
      const runId = activeTab === 'run' ? runReportMutation.data?.runId : compareMutation.data?.runId;
      const response = await prefundingApi.saveReport(runId);
      return response.data;
    },
    onSuccess: () => {
      setIsSaved(true);
    },
  });

  // Fetch records by run ID mutation (for update status)
  const fetchRecordsMutation = useMutation({
    mutationFn: async () => {
      const response = await prefundingApi.getRecordsByRunId(tenantCode, updateRunId);
      return response.data;
    },
  });

  // Update approval status mutation
  const updateStatusMutation = useMutation({
    mutationFn: async () => {
      // Build updates array from matched records - use consumer_code if that was the match key
      const matchBy = uploadedFileData?.matchBy || 'consumer_code';
      const updates = matchedRecords
        .filter(r => r.newApprovalStatus)
        .map(r => ({
          consumer_code: r.consumer_code,
          member_id: r.member_id,
          approval_status: r.newApprovalStatus
        }));
      // Pass full records for S3 export
      const fullRecords = matchedRecords.map(r => ({
        ...r,
        newApprovalStatus: r.newApprovalStatus,
        hasUpdate: r.hasUpdate
      }));
      const response = await prefundingApi.updateApprovalStatus(tenantCode, updateRunId, updates, matchBy, fullRecords);
      return response.data;
    },
    onSuccess: () => {
      setIsUpdateSaved(true);
    },
  });

  // Handle file upload for update status
  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsProcessingFile(true);
    setUploadedFileData(null);
    setMatchedRecords([]);

    try {
      // First fetch records for the selected run
      const recordsResponse = await prefundingApi.getRecordsByRunId(tenantCode, updateRunId);
      const existingRecords = recordsResponse.data?.records || [];

      // Parse the uploaded file
      const reader = new FileReader();
      reader.onload = async (e) => {
        const data = e.target.result;
        let parsedData = [];
        let hasConsumerCode = false;

        if (file.name.endsWith('.csv')) {
          // Parse CSV
          const lines = data.split('\n');
          const headers = lines[0].toLowerCase().split(',').map(h => h.trim().replace(/['"]/g, ''));

          // Priority: consumer_code > member_id
          const consumerCodeIndex = headers.findIndex(h => h.includes('consumer') && h.includes('code'));
          const memberIdIndex = headers.findIndex(h => h.includes('member') && h.includes('id'));
          const statusIndex = headers.findIndex(h => h.includes('approval') || h.includes('status'));

          hasConsumerCode = consumerCodeIndex !== -1;
          const keyIndex = hasConsumerCode ? consumerCodeIndex : memberIdIndex;

          if (keyIndex === -1) {
            alert('CSV must have a column containing "consumer_code" or "member_id"');
            return;
          }
          if (statusIndex === -1) {
            alert('CSV must have a column containing "approval" or "status"');
            return;
          }

          for (let i = 1; i < lines.length; i++) {
            const values = lines[i].split(',').map(v => v.trim().replace(/['"]/g, ''));
            if (values[keyIndex]) {
              parsedData.push({
                key: values[keyIndex],
                approval_status: values[statusIndex]?.toUpperCase() || 'APPROVED'
              });
            }
          }
        } else {
          // Parse Excel
          const workbook = XLSX.read(data, { type: 'binary' });
          const sheetName = workbook.SheetNames[0];
          const sheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(sheet);

          // Priority: consumer_code > member_id
          const firstRow = jsonData[0] || {};
          const consumerCodeKey = Object.keys(firstRow).find(k => k.toLowerCase().includes('consumer') && k.toLowerCase().includes('code'));
          const memberIdKey = Object.keys(firstRow).find(k => k.toLowerCase().includes('member') && k.toLowerCase().includes('id'));
          const statusKey = Object.keys(firstRow).find(k => k.toLowerCase().includes('approval') || k.toLowerCase().includes('status'));

          hasConsumerCode = !!consumerCodeKey;
          const keyField = hasConsumerCode ? consumerCodeKey : memberIdKey;

          if (!keyField) {
            alert('Excel must have a column containing "consumer_code" or "member_id"');
            return;
          }

          parsedData = jsonData.map(row => ({
            key: String(row[keyField] || ''),
            approval_status: String(row[statusKey] || 'APPROVED').toUpperCase()
          })).filter(r => r.key);
        }

        setUploadedFileData({
          fileName: file.name,
          records: parsedData,
          matchBy: hasConsumerCode ? 'consumer_code' : 'member_id'
        });

        // Match with existing records - use consumer_code if available, else member_id
        const fileMap = new Map(parsedData.map(r => [String(r.key), r.approval_status]));

        const matched = existingRecords.map(record => {
          const matchKey = hasConsumerCode ? String(record.consumer_code) : String(record.member_id);
          const newStatus = fileMap.get(matchKey);
          return {
            ...record,
            newApprovalStatus: newStatus || null,
            hasUpdate: !!newStatus && newStatus !== record.approval_status
          };
        }).filter(r => r.newApprovalStatus); // Only show records that have a match in the file

        setMatchedRecords(matched);
        setUpdatePage(1);
        setIsUpdateSaved(false);
        setIsProcessingFile(false);
      };

      reader.onerror = () => {
        setIsProcessingFile(false);
        alert('Error reading file');
      };

      if (file.name.endsWith('.csv')) {
        reader.readAsText(file);
      } else {
        reader.readAsBinaryString(file);
      }
    } catch (error) {
      setIsProcessingFile(false);
      alert('Error processing file: ' + error.message);
    }
  };

  const handleRunReport = () => {
    if (!tenantCode) {
      alert('Please select a tenant');
      return;
    }
    setCurrentPage(1);
    setIsSaved(false);
    runReportMutation.mutate();
  };

  const handleRunCompare = () => {
    if (!tenantCode) {
      alert('Please select a tenant');
      return;
    }
    if (!previousRunId) {
      alert('Please select a previous run to compare against');
      return;
    }
    setCurrentPage(1);
    setIsSaved(false);
    compareMutation.mutate();
  };

  const handleSaveToDatabase = () => {
    const data = activeTab === 'run' ? runReportMutation.data : compareMutation.data;
    if (!data?.records?.length) {
      alert('No records to save');
      return;
    }
    if (isSaved) {
      alert('This report has already been saved');
      return;
    }
    saveReportMutation.mutate();
  };

  // Get current data based on active tab
  const currentMutation = activeTab === 'run' ? runReportMutation : compareMutation;
  const reportData = currentMutation.data;
  const records = reportData?.records || [];
  const summary = reportData?.summary || {};

  // Pagination
  const totalPages = Math.ceil(records.length / pageSize);
  const paginatedRecords = records.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const getExportData = () => {
    if (!records.length) return null;

    const isCompare = activeTab === 'compare';
    // Group ID sits after Plan ID — matches the column order produced by the
    // backend's S3 CSV (generateCSV / generateStatusUpdateCSV in prefundingService.js).
    const headers = isCompare
      ? ['Run ID', 'Tenant Code', 'Funding Date', 'Consumer Code', 'Member ID', 'Plan ID',
         'Group ID', 'Wallet Type', 'Purse Name', 'Funding Amount', 'Rule Number',
         'Modification Type', 'Changed Fields', 'Approval Status', 'Previous Run ID']
      : ['Run ID', 'Tenant Code', 'Funding Date', 'Consumer Code', 'Member ID', 'Plan ID',
         'Group ID', 'Plan Start Date', 'Plan Term Date', 'Wallet Type', 'Purse Name',
         'Funding Amount', 'Rule Number', 'Approval Status', 'Funding Status'];

    const rows = records.map(r => isCompare
      ? [r.run_id, r.tenant_code, formatDate(r.funding_date), r.consumer_code, r.member_id,
         r.plan_id, r.group_id || '', r.wallet_type_code, r.purse_name,
         parseFloat(r.funding_amount || 0).toFixed(2), r.rule_number, r.modification_type,
         r.changed_fields || '', r.approval_status, r.previous_run_id]
      : [r.run_id, r.tenant_code, formatDate(r.funding_date), r.consumer_code, r.member_id,
         r.plan_id, r.group_id || '', formatDate(r.plan_start_date), formatDate(r.plan_term_date),
         r.wallet_type_code, r.purse_name, parseFloat(r.funding_amount || 0).toFixed(2),
         r.rule_number, r.approval_status, r.funding_status]);

    return { headers, rows, records };
  };

  const formatDate = (value) => {
    if (!value) return '';
    if (typeof value === 'string' && value.includes('T')) return value.split('T')[0];
    return String(value);
  };

  const getFileName = (extension) => {
    const runId = reportData?.runId || 'export';
    const prefix = activeTab === 'compare' ? 'prefunding_compare' : 'prefunding_report';
    return `${prefix}_${runAsOf}_${runId}.${extension}`;
  };

  // CSV Export
  const handleExportCSV = () => {
    const data = getExportData();
    if (!data) return;

    const csvContent = [
      data.headers.join(','),
      ...data.rows.map(row => row.map(v => `"${v || ''}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = getFileName('csv');
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
    setShowExportMenu(false);
  };

  // Excel Export
  const handleExportExcel = () => {
    const data = getExportData();
    if (!data) return;

    const wb = XLSX.utils.book_new();
    const wsData = XLSX.utils.aoa_to_sheet([data.headers, ...data.rows]);

    const colWidths = data.headers.map((h, i) => {
      const maxLength = Math.max(h.length, ...data.rows.map(row => String(row[i] || '').length));
      return { wch: Math.min(maxLength + 2, 40) };
    });
    wsData['!cols'] = colWidths;

    XLSX.utils.book_append_sheet(wb, wsData, activeTab === 'compare' ? 'Compare Results' : 'Prefunding Records');
    XLSX.writeFile(wb, getFileName('xlsx'));
    setShowExportMenu(false);
  };

  // PDF Export
  const handleExportPDF = () => {
    const data = getExportData();
    if (!data) return;

    const doc = new jsPDF('l', 'mm', 'a4');

    doc.setFontSize(18);
    doc.setTextColor(33, 37, 41);
    doc.text(activeTab === 'compare' ? 'Prefunding Compare Report' : 'Prefunding Report', 14, 15);

    doc.setFontSize(10);
    doc.setTextColor(108, 117, 125);
    doc.text(`Run ID: ${reportData?.runId}`, 14, 22);
    if (activeTab === 'compare') {
      doc.text(`Previous Run ID: ${reportData?.previousRunId}`, 14, 27);
      doc.text(`Date: ${runAsOf}`, 14, 32);
    } else {
      doc.text(`Tenant: ${reportData?.tenantCode}`, 14, 27);
      doc.text(`Date: ${runAsOf}`, 14, 32);
    }

    const pdfHeaders = activeTab === 'compare'
      ? ['Member ID', 'Plan ID', 'Purse', 'Amount', 'Type', 'Changed', 'Status']
      : ['Member ID', 'Plan ID', 'Wallet', 'Purse', 'Amount', 'Rule #', 'Status'];

    const pdfRows = data.records.map(r => activeTab === 'compare'
      ? [r.member_id, r.plan_id, r.purse_name, `$${parseFloat(r.funding_amount || 0).toFixed(2)}`,
         r.modification_type, r.changed_fields?.substring(0, 20) || '', r.approval_status]
      : [r.member_id, r.plan_id, r.wallet_type_code, r.purse_name,
         `$${parseFloat(r.funding_amount || 0).toFixed(2)}`, r.rule_number, r.approval_status]);

    doc.autoTable({
      head: [pdfHeaders],
      body: pdfRows,
      startY: 40,
      styles: { fontSize: 7, cellPadding: 1.5 },
      headStyles: { fillColor: [59, 130, 246], textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      margin: { left: 14, right: 14 },
    });

    doc.save(getFileName('pdf'));
    setShowExportMenu(false);
  };

  // Modification type badge color
  const getModificationBadge = (type) => {
    const colors = {
      NEW: 'bg-green-100 text-green-700',
      UNCHANGED: 'bg-gray-100 text-gray-700',
      MODIFIED: 'bg-amber-100 text-amber-700',
      REMOVED: 'bg-red-100 text-red-700',
    };
    return colors[type] || 'bg-gray-100 text-gray-700';
  };

  const getApprovalBadge = (status) => {
    const colors = {
      APPROVED: 'bg-green-100 text-green-700',
      ON_HOLD: 'bg-amber-100 text-amber-700',
      REJECTED: 'bg-red-100 text-red-700',
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
            <DollarSign className="w-6 h-6 text-green-600" />
            Prefunding Report
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Generate prefunding analysis for eligible members
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowHelp(!showHelp)}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors ${
              showHelp ? 'bg-purple-50 border-purple-200 text-purple-700' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
            }`}
          >
            <HelpCircle className="w-4 h-4" />
            How to Use
          </button>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors ${
              showFilters ? 'bg-blue-50 border-blue-200 text-blue-700' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
            }`}
          >
            <Filter className="w-4 h-4" />
            Filters
          </button>
          {activeTab !== 'update' && (
            <>
              <button
                onClick={handleSaveToDatabase}
                disabled={!records.length || isSaved || saveReportMutation.isPending}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                  isSaved ? 'bg-gray-100 text-gray-500 border border-gray-200 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed'
                }`}
              >
                {saveReportMutation.isPending ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Saving...</>
                ) : isSaved ? (
                  <><CheckCircle className="w-4 h-4" /> Saved</>
                ) : (
                  <><Database className="w-4 h-4" /> Save Report</>
                )}
              </button>
              <div className="relative" ref={exportMenuRef}>
                <button
                  onClick={() => setShowExportMenu(!showExportMenu)}
                  disabled={!records.length}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Download className="w-4 h-4" /> Export <ChevronDown className="w-4 h-4" />
                </button>
                {showExportMenu && records.length > 0 && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                    <button onClick={handleExportCSV} className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 rounded-t-lg">
                      <FileText className="w-4 h-4 text-green-600" /> Export as CSV
                    </button>
                    <button onClick={handleExportExcel} className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 border-t border-gray-100">
                      <FileSpreadsheet className="w-4 h-4 text-emerald-600" /> Export as Excel
                    </button>
                    <button onClick={handleExportPDF} className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 border-t border-gray-100 rounded-b-lg">
                      <File className="w-4 h-4 text-red-600" /> Export as PDF
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* How to Use Guide */}
      {showHelp && (
        <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg border border-purple-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <BookOpen className="w-5 h-5 text-purple-600" />
            <h3 className="text-lg font-semibold text-purple-900">How to Use Prefunding Report</h3>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Main Workflow */}
            <div>
              <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Validation Workflow</h4>
              <div className="space-y-3">
                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-7 h-7 bg-purple-600 text-white rounded-full flex items-center justify-center text-sm font-bold">1</div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">Run the Report</p>
                    <p className="text-xs text-gray-600">Select tenant and run date (always use beginning of the month for monthly validation)</p>
                  </div>
                </div>

                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-7 h-7 bg-purple-600 text-white rounded-full flex items-center justify-center text-sm font-bold">2</div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">Export Data (Optional)</p>
                    <p className="text-xs text-gray-600">Download as CSV, Excel, or PDF for offline review</p>
                  </div>
                </div>

                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-7 h-7 bg-purple-600 text-white rounded-full flex items-center justify-center text-sm font-bold">3</div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">Save Report</p>
                    <p className="text-xs text-gray-600">Saves to Redshift with unique Run ID. <span className="text-amber-600 font-medium">Allow 10-20 mins for data refresh.</span></p>
                  </div>
                </div>

                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-7 h-7 bg-purple-600 text-white rounded-full flex items-center justify-center text-sm font-bold">4</div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">Validate Data</p>
                    <p className="text-xs text-gray-600">Use <span className="font-medium">Tech Support → Custom Query</span> to query eligibility, funding rules, or other data</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Status Update Workflow */}
            <div>
              <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Update Status Workflow</h4>
              <div className="space-y-3">
                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-7 h-7 bg-indigo-600 text-white rounded-full flex items-center justify-center text-sm font-bold">5</div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">Prepare Spreadsheet</p>
                    <p className="text-xs text-gray-600">Create file with <span className="font-mono bg-gray-100 px-1 rounded">MEMBER ID</span>, <span className="font-mono bg-gray-100 px-1 rounded">CONSUMER CODE</span>, and <span className="font-mono bg-gray-100 px-1 rounded">APPROVAL STATUS</span></p>
                    <p className="text-xs text-gray-500 mt-1">Status values: APPROVED, REJECTED, ON_HOLD</p>
                  </div>
                </div>

                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-7 h-7 bg-indigo-600 text-white rounded-full flex items-center justify-center text-sm font-bold">6</div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">Upload File</p>
                    <p className="text-xs text-gray-600">Go to <span className="font-medium">Update Funding Status</span> tab, select Run ID, and upload your spreadsheet</p>
                  </div>
                </div>

                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-7 h-7 bg-indigo-600 text-white rounded-full flex items-center justify-center text-sm font-bold">7</div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">Review & Apply Changes</p>
                    <p className="text-xs text-gray-600">LENS displays previous vs new status. Validate and click <span className="font-medium">Apply Changes</span> to update Redshift</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Compare Section */}
          <div className="mt-5 pt-4 border-t border-purple-200">
            <div className="flex items-start gap-3">
              <GitCompare className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-gray-900">Compare with Previous Run</p>
                <p className="text-xs text-gray-600">
                  Use the <span className="font-medium">Compare with Previous Run</span> tab to compare the latest report against a previous Run ID.
                  This helps identify NEW, MODIFIED, REMOVED, and UNCHANGED records for validation and analysis.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => { setActiveTab('run'); setCurrentPage(1); }}
            className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
              activeTab === 'run'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Play className="w-4 h-4" /> Run Report
          </button>
          <button
            onClick={() => { setActiveTab('compare'); setCurrentPage(1); }}
            className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
              activeTab === 'compare'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <GitCompare className="w-4 h-4" /> Compare with Previous Run
          </button>
          <button
            onClick={() => { setActiveTab('update'); setCurrentPage(1); setMatchedRecords([]); setUploadedFileData(null); }}
            className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
              activeTab === 'update'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Upload className="w-4 h-4" /> Update Funding Status
          </button>
        </nav>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Building className="w-4 h-4 inline mr-1" /> Tenant
              </label>
              <select
                value={tenantCode}
                onChange={(e) => { setTenantCode(e.target.value); setPreviousRunId(''); }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                disabled={tenantsLoading}
              >
                <option value="">Select a tenant...</option>
                {tenants.map((t) => (
                  <option key={t.tenant_code} value={t.tenant_code}>
                    {t.tenant_name || t.tenant_code}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Calendar className="w-4 h-4 inline mr-1" /> Run As Of Date
              </label>
              <input
                type="date"
                value={runAsOf}
                onChange={(e) => setRunAsOf(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            {activeTab === 'compare' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Hash className="w-4 h-4 inline mr-1" /> Previous Run
                </label>
                <select
                  value={previousRunId}
                  onChange={(e) => setPreviousRunId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  disabled={runHistoryLoading || !tenantCode}
                >
                  <option value="">Select previous run...</option>
                  {runHistory.map((r) => (
                    <option key={r.run_id} value={r.run_id}>
                      {r.funding_date?.split('T')[0]} - {r.run_id.substring(0, 30)}...
                    </option>
                  ))}
                </select>
              </div>
            )}
            {activeTab === 'update' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <Hash className="w-4 h-4 inline mr-1" /> Run ID
                  </label>
                  <select
                    value={updateRunId}
                    onChange={(e) => { setUpdateRunId(e.target.value); setMatchedRecords([]); setUploadedFileData(null); }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    disabled={runHistoryLoading || !tenantCode}
                  >
                    <option value="">Select run...</option>
                    {runHistory.map((r) => (
                      <option key={r.run_id} value={r.run_id}>
                        {r.funding_date?.split('T')[0]} - {r.run_id.substring(0, 30)}...
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <FileUp className="w-4 h-4 inline mr-1" /> Upload File
                  </label>
                  <input
                    type="file"
                    ref={fileInputRef}
                    accept=".csv,.xlsx,.xls"
                    onChange={handleFileUpload}
                    disabled={!tenantCode || !updateRunId}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm file:mr-4 file:py-1 file:px-3 file:rounded file:border-0 file:text-sm file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 disabled:opacity-50"
                  />
                  <p className="text-xs text-gray-500 mt-1">CSV or Excel with Member ID and Approval Status columns</p>
                </div>
              </>
            )}
            {activeTab !== 'update' && (
              <div className={`flex items-end ${activeTab === 'compare' ? '' : 'md:col-span-2'}`}>
                <button
                  onClick={activeTab === 'run' ? handleRunReport : handleRunCompare}
                  disabled={currentMutation.isPending || !tenantCode || (activeTab === 'compare' && !previousRunId)}
                  className="flex items-center justify-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {currentMutation.isPending ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Running...</>
                  ) : activeTab === 'run' ? (
                    <><Play className="w-4 h-4" /> Run Report</>
                  ) : (
                    <><GitCompare className="w-4 h-4" /> Run Compare</>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Error Messages */}
      {currentMutation.isError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-red-800">Error running {activeTab === 'run' ? 'report' : 'compare'}</p>
            <p className="text-sm text-red-600">{currentMutation.error?.message}</p>
          </div>
        </div>
      )}

      {saveReportMutation.isError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-red-800">Error uploading to S3</p>
            <p className="text-sm text-red-600">{saveReportMutation.error?.message}</p>
          </div>
        </div>
      )}

      {isSaved && saveReportMutation.data && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-green-800">Report saved successfully</p>
              <p className="text-sm text-green-600">{saveReportMutation.data.recordCount} records uploaded</p>
              <p className="text-xs text-green-500 mt-1 font-mono">
                s3://{saveReportMutation.data.s3Bucket}/{saveReportMutation.data.s3Key}
              </p>
            </div>
          </div>
          <div className="mt-3 pt-3 border-t border-green-200 flex items-center gap-2">
            <Loader2 className="w-4 h-4 text-amber-600 animate-spin" />
            <p className="text-sm text-amber-700">
              Data will be available in Redshift within 10-20 minutes
            </p>
          </div>
        </div>
      )}

      {/* Query Display - Collapsible */}
      {reportData?.query && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <button
            onClick={() => setShowQuery(!showQuery)}
            className="w-full px-4 py-3 bg-slate-50 border-b border-gray-200 flex items-center justify-between hover:bg-slate-100 transition-colors"
          >
            <h3 className="text-sm font-medium text-gray-700 flex items-center gap-2">
              <Code className="w-4 h-4 text-slate-600" />
              SQL Query
            </h3>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">Click to {showQuery ? 'hide' : 'show'}</span>
              {showQuery ? <ChevronUp className="w-4 h-4 text-gray-500" /> : <ChevronDown className="w-4 h-4 text-gray-500" />}
            </div>
          </button>
          {showQuery && (
            <div className="p-4 bg-slate-900 overflow-x-auto">
              <pre className="text-xs text-green-400 font-mono whitespace-pre-wrap">{reportData.query}</pre>
            </div>
          )}
        </div>
      )}

      {/* Summary Cards */}
      {reportData && (
        <div className={`grid gap-4 ${activeTab === 'compare' ? 'grid-cols-2 md:grid-cols-6' : 'grid-cols-2 md:grid-cols-4'}`}>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-50 rounded-lg">
                <FileText className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{summary.totalRecords?.toLocaleString() || 0}</p>
                <p className="text-sm text-gray-500">Total Records</p>
              </div>
            </div>
          </div>
          {activeTab === 'compare' ? (
            <>
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-50 rounded-lg"><Plus className="w-5 h-5 text-green-600" /></div>
                  <div>
                    <p className="text-2xl font-bold text-green-600">{summary.new?.toLocaleString() || 0}</p>
                    <p className="text-sm text-gray-500">New</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-gray-50 rounded-lg"><RefreshCw className="w-5 h-5 text-gray-600" /></div>
                  <div>
                    <p className="text-2xl font-bold text-gray-600">{summary.unchanged?.toLocaleString() || 0}</p>
                    <p className="text-sm text-gray-500">Unchanged</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-amber-50 rounded-lg"><Edit3 className="w-5 h-5 text-amber-600" /></div>
                  <div>
                    <p className="text-2xl font-bold text-amber-600">{summary.modified?.toLocaleString() || 0}</p>
                    <p className="text-sm text-gray-500">Modified</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-red-50 rounded-lg"><Minus className="w-5 h-5 text-red-600" /></div>
                  <div>
                    <p className="text-2xl font-bold text-red-600">{summary.removed?.toLocaleString() || 0}</p>
                    <p className="text-sm text-gray-500">Removed</p>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-50 rounded-lg"><Users className="w-5 h-5 text-green-600" /></div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{summary.uniqueMembers?.toLocaleString() || 0}</p>
                  <p className="text-sm text-gray-500">Unique Members</p>
                </div>
              </div>
            </div>
          )}
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-50 rounded-lg"><DollarSign className="w-5 h-5 text-amber-600" /></div>
              <div>
                <p className="text-2xl font-bold text-gray-900">${parseFloat(summary.totalFundingAmount || 0).toLocaleString()}</p>
                <p className="text-sm text-gray-500">Total Funding</p>
              </div>
            </div>
          </div>
          {activeTab === 'run' && reportData?.runId && (
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-50 rounded-lg"><Hash className="w-5 h-5 text-purple-600" /></div>
                <div>
                  <p className="text-sm font-mono text-gray-700 truncate max-w-[200px]" title={reportData.runId}>{reportData.runId}</p>
                  <p className="text-sm text-gray-500">Run ID</p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Funding by Purse Name - Only for Run Report */}
      {reportData && activeTab === 'run' && summary.purseBreakdown && Object.keys(summary.purseBreakdown).length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <h3 className="text-sm font-medium text-gray-700 mb-4 flex items-center gap-2">
            <Wallet className="w-4 h-4 text-blue-600" />
            Funding by Purse Name
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(summary.purseBreakdown).map(([purseName, data]) => (
              <div key={purseName} className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                <p className="text-sm font-medium text-gray-900">{purseName}</p>
                <p className="text-lg font-bold text-green-600">${parseFloat(data.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                <p className="text-xs text-gray-500">{data.count?.toLocaleString()} records</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Results Table */}
      {reportData && activeTab === 'run' && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
            <h3 className="text-sm font-medium text-gray-700">
              Prefunding Records ({records.length.toLocaleString()} total)
            </h3>
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <CheckCircle className="w-4 h-4 text-green-500" />
              Executed at {new Date(reportData.executedAt).toLocaleString()}
            </div>
          </div>

          {records.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No records found for the selected criteria</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Member ID</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Consumer Code</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Plan ID</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Group ID</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Wallet</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Purse</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rule #</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {paginatedRecords.map((record, idx) => (
                      <tr key={`${record.enrollment_key}-${record.rule_number}-${idx}`} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{record.member_id}</td>
                        <td className="px-4 py-3 text-sm text-gray-500 font-mono text-xs">{record.consumer_code?.substring(0, 20)}...</td>
                        <td className="px-4 py-3 text-sm text-gray-500">{record.plan_id}</td>
                        <td className="px-4 py-3 text-sm text-gray-500">{record.group_id || '-'}</td>
                        <td className="px-4 py-3">
                          <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-700">
                            {record.wallet_type_code}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">{record.purse_name}</td>
                        <td className="px-4 py-3 text-sm font-medium text-green-600 text-right">
                          ${parseFloat(record.funding_amount || 0).toFixed(2)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">{record.rule_number}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getApprovalBadge(record.approval_status)}`}>
                            {record.approval_status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
                  <div className="text-sm text-gray-700">
                    Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, records.length)} of {records.length}
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
                      className="p-1 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed">
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <span className="text-sm text-gray-700">Page {currentPage} of {totalPages}</span>
                    <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}
                      className="p-1 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed">
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Compare Results - Split View */}
      {reportData && activeTab === 'compare' && (() => {
        const changedRecords = records.filter(r => r.modification_type !== 'UNCHANGED');
        const unchangedRecords = records.filter(r => r.modification_type === 'UNCHANGED');
        const changedTotalPages = Math.ceil(changedRecords.length / pageSize);
        const unchangedTotalPages = Math.ceil(unchangedRecords.length / pageSize);
        const paginatedChanged = changedRecords.slice((currentPage - 1) * pageSize, currentPage * pageSize);
        const paginatedUnchanged = unchangedRecords.slice((unchangedPage - 1) * pageSize, unchangedPage * pageSize);

        return (
          <>
            {/* Changes Section */}
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div className="px-4 py-3 bg-gradient-to-r from-amber-50 to-orange-50 border-b border-gray-200 flex items-center justify-between">
                <h3 className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <Edit3 className="w-4 h-4 text-amber-600" />
                  Changes ({changedRecords.length.toLocaleString()} records)
                  <span className="text-xs text-gray-500 ml-2">
                    NEW: {summary.new || 0} | MODIFIED: {summary.modified || 0} | REMOVED: {summary.removed || 0}
                  </span>
                </h3>
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  Executed at {new Date(reportData.executedAt).toLocaleString()}
                </div>
              </div>

              {changedRecords.length === 0 ? (
                <div className="text-center py-8">
                  <CheckCircle className="w-10 h-10 text-green-300 mx-auto mb-3" />
                  <p className="text-gray-500">No changes detected - all records are unchanged</p>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Member ID</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Consumer Code</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Plan ID</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Group ID</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Purse</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Changed Fields</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {paginatedChanged.map((record, idx) => (
                          <tr key={`changed-${record.enrollment_key}-${record.rule_number}-${idx}`}
                              className={`hover:bg-gray-50 ${
                                record.modification_type === 'NEW' ? 'bg-green-50/30' :
                                record.modification_type === 'MODIFIED' ? 'bg-amber-50/30' :
                                record.modification_type === 'REMOVED' ? 'bg-red-50/30' : ''
                              }`}>
                            <td className="px-4 py-3">
                              <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getModificationBadge(record.modification_type)}`}>
                                {record.modification_type}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm font-medium text-gray-900">{record.member_id}</td>
                            <td className="px-4 py-3 text-sm text-gray-500 font-mono text-xs">{record.consumer_code?.substring(0, 20)}...</td>
                            <td className="px-4 py-3 text-sm text-gray-500">{record.plan_id}</td>
                            <td className="px-4 py-3 text-sm text-gray-500">{record.group_id || '-'}</td>
                            <td className="px-4 py-3 text-sm text-gray-500">{record.purse_name}</td>
                            <td className="px-4 py-3 text-sm font-medium text-green-600 text-right">
                              ${parseFloat(record.funding_amount || 0).toFixed(2)}
                            </td>
                            <td className="px-4 py-3 text-sm text-amber-600 max-w-[200px]" title={record.changed_fields}>
                              {record.changed_fields || '-'}
                            </td>
                            <td className="px-4 py-3">
                              <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getApprovalBadge(record.approval_status)}`}>
                                {record.approval_status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {changedTotalPages > 1 && (
                    <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
                      <div className="text-sm text-gray-700">
                        Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, changedRecords.length)} of {changedRecords.length}
                      </div>
                      <div className="flex items-center gap-2">
                        <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
                          className="p-1 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed">
                          <ChevronLeft className="w-5 h-5" />
                        </button>
                        <span className="text-sm text-gray-700">Page {currentPage} of {changedTotalPages}</span>
                        <button onClick={() => setCurrentPage(p => Math.min(changedTotalPages, p + 1))} disabled={currentPage === changedTotalPages}
                          className="p-1 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed">
                          <ChevronRight className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Unchanged Section - Collapsible */}
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <button
                onClick={() => setShowUnchanged(!showUnchanged)}
                className="w-full px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between hover:bg-gray-100 transition-colors"
              >
                <h3 className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  {showUnchanged ? <EyeOff className="w-4 h-4 text-gray-500" /> : <Eye className="w-4 h-4 text-gray-500" />}
                  Unchanged Records ({unchangedRecords.length.toLocaleString()})
                </h3>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">Click to {showUnchanged ? 'hide' : 'show'}</span>
                  {showUnchanged ? <ChevronUp className="w-4 h-4 text-gray-500" /> : <ChevronDown className="w-4 h-4 text-gray-500" />}
                </div>
              </button>

              {showUnchanged && (
                <>
                  {unchangedRecords.length === 0 ? (
                    <div className="text-center py-8">
                      <FileText className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                      <p className="text-gray-500">No unchanged records</p>
                    </div>
                  ) : (
                    <>
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Member ID</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Consumer Code</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Plan ID</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Group ID</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Wallet</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Purse</th>
                              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rule #</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {paginatedUnchanged.map((record, idx) => (
                              <tr key={`unchanged-${record.enrollment_key}-${record.rule_number}-${idx}`} className="hover:bg-gray-50">
                                <td className="px-4 py-3 text-sm font-medium text-gray-900">{record.member_id}</td>
                                <td className="px-4 py-3 text-sm text-gray-500 font-mono text-xs">{record.consumer_code?.substring(0, 20)}...</td>
                                <td className="px-4 py-3 text-sm text-gray-500">{record.plan_id}</td>
                                <td className="px-4 py-3 text-sm text-gray-500">{record.group_id || '-'}</td>
                                <td className="px-4 py-3">
                                  <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-700">
                                    {record.wallet_type_code}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-500">{record.purse_name}</td>
                                <td className="px-4 py-3 text-sm font-medium text-green-600 text-right">
                                  ${parseFloat(record.funding_amount || 0).toFixed(2)}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-500">{record.rule_number}</td>
                                <td className="px-4 py-3">
                                  <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getApprovalBadge(record.approval_status)}`}>
                                    {record.approval_status}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {unchangedTotalPages > 1 && (
                        <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
                          <div className="text-sm text-gray-700">
                            Showing {((unchangedPage - 1) * pageSize) + 1} to {Math.min(unchangedPage * pageSize, unchangedRecords.length)} of {unchangedRecords.length}
                          </div>
                          <div className="flex items-center gap-2">
                            <button onClick={() => setUnchangedPage(p => Math.max(1, p - 1))} disabled={unchangedPage === 1}
                              className="p-1 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed">
                              <ChevronLeft className="w-5 h-5" />
                            </button>
                            <span className="text-sm text-gray-700">Page {unchangedPage} of {unchangedTotalPages}</span>
                            <button onClick={() => setUnchangedPage(p => Math.min(unchangedTotalPages, p + 1))} disabled={unchangedPage === unchangedTotalPages}
                              className="p-1 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed">
                              <ChevronRight className="w-5 h-5" />
                            </button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          </>
        );
      })()}

      {/* Initial State */}
      {!reportData && !currentMutation.isPending && activeTab !== 'update' && (
        <div className="bg-white rounded-lg border border-gray-200 p-8">
          <div className="text-center py-12">
            {activeTab === 'run' ? (
              <>
                <DollarSign className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Prefunding Report</h3>
                <p className="text-gray-500 max-w-md mx-auto">
                  Select a tenant and date, then click "Run Report" to generate the prefunding analysis.
                </p>
              </>
            ) : (
              <>
                <GitCompare className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Compare with Previous Run</h3>
                <p className="text-gray-500 max-w-md mx-auto">
                  Select a tenant, date, and previous run to compare. The comparison will show NEW, UNCHANGED, MODIFIED, and REMOVED records.
                </p>
              </>
            )}
          </div>
        </div>
      )}

      {/* Update Status Tab */}
      {activeTab === 'update' && (
        <>
          {/* Processing Spinner */}
          {isProcessingFile && (
            <div className="bg-white rounded-lg border border-gray-200 p-12">
              <div className="flex flex-col items-center justify-center">
                <Loader2 className="w-12 h-12 text-blue-600 animate-spin mb-4" />
                <p className="text-lg font-medium text-gray-900">Processing file...</p>
                <p className="text-sm text-gray-500 mt-1">Fetching records and matching data</p>
              </div>
            </div>
          )}

          {/* Upload Status Info */}
          {!isProcessingFile && uploadedFileData && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FileSpreadsheet className="w-5 h-5 text-blue-600" />
                <div>
                  <p className="text-sm font-medium text-blue-800">File uploaded: {uploadedFileData.fileName}</p>
                  <p className="text-sm text-blue-600">
                    {uploadedFileData.records.length} records found, {matchedRecords.length} matched by {uploadedFileData.matchBy === 'consumer_code' ? 'Consumer Code' : 'Member ID'}
                  </p>
                </div>
              </div>
              {matchedRecords.length > 0 && !isUpdateSaved && (
                <button
                  onClick={() => updateStatusMutation.mutate()}
                  disabled={updateStatusMutation.isPending}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                >
                  {updateStatusMutation.isPending ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Saving...</>
                  ) : (
                    <><Save className="w-4 h-4" /> Apply Updates</>
                  )}
                </button>
              )}
            </div>
          )}

          {/* Update Success Message */}
          {!isProcessingFile && isUpdateSaved && updateStatusMutation.data && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-green-800">Approval status updated and exported to S3</p>
                <p className="text-sm text-green-600">
                  {updateStatusMutation.data.updatedCount} of {updateStatusMutation.data.totalRequested} records updated
                </p>
                {updateStatusMutation.data.s3 && !updateStatusMutation.data.s3.error && (
                  <p className="text-xs text-green-500 mt-1 font-mono">
                    s3://{updateStatusMutation.data.s3.bucket}/{updateStatusMutation.data.s3.key}
                  </p>
                )}
                {updateStatusMutation.data.s3?.error && (
                  <p className="text-xs text-amber-600 mt-1">
                    S3 upload failed: {updateStatusMutation.data.s3.error}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Update Error Message */}
          {!isProcessingFile && updateStatusMutation.isError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-red-800">Error updating status</p>
                <p className="text-sm text-red-600">{updateStatusMutation.error?.message}</p>
              </div>
            </div>
          )}

          {/* Matched Records Summary */}
          {!isProcessingFile && matchedRecords.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-50 rounded-lg">
                    <FileText className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">{matchedRecords.length}</p>
                    <p className="text-sm text-gray-500">Matched Records</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-amber-50 rounded-lg">
                    <Edit3 className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-amber-600">{matchedRecords.filter(r => r.hasUpdate).length}</p>
                    <p className="text-sm text-gray-500">Will Change</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-gray-50 rounded-lg">
                    <RefreshCw className="w-5 h-5 text-gray-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-600">{matchedRecords.filter(r => !r.hasUpdate).length}</p>
                    <p className="text-sm text-gray-500">No Change</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-50 rounded-lg">
                    <Hash className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm font-mono text-gray-700 truncate max-w-[150px]" title={updateRunId}>{updateRunId?.substring(0, 20)}...</p>
                    <p className="text-sm text-gray-500">Run ID</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Matched Records Table */}
          {!isProcessingFile && matchedRecords.length > 0 && (() => {
            const updateTotalPages = Math.ceil(matchedRecords.length / pageSize);
            const paginatedUpdate = matchedRecords.slice((updatePage - 1) * pageSize, updatePage * pageSize);

            return (
              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <div className="px-4 py-3 bg-gradient-to-r from-purple-50 to-indigo-50 border-b border-gray-200 flex items-center justify-between">
                  <h3 className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <Upload className="w-4 h-4 text-purple-600" />
                    Records to Update ({matchedRecords.length.toLocaleString()} matched)
                  </h3>
                </div>

                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Member ID</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Consumer Code</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Plan ID</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Group ID</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Plan Dates</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Wallet</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Purse</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rule #</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Current Status</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">New Status</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Change</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {paginatedUpdate.map((record, idx) => (
                        <tr key={`update-${record.consumer_code}-${record.rule_number}-${idx}`}
                            className={`hover:bg-gray-50 ${record.hasUpdate ? 'bg-amber-50/30' : ''}`}>
                          <td className="px-4 py-3 text-sm font-medium text-gray-900">{record.member_id}</td>
                          <td className="px-4 py-3 text-sm text-gray-500 font-mono text-xs">{record.consumer_code?.substring(0, 20)}...</td>
                          <td className="px-4 py-3 text-sm text-gray-500">{record.plan_id}</td>
                          <td className="px-4 py-3 text-sm text-gray-500">{record.group_id || '-'}</td>
                          <td className="px-4 py-3 text-sm text-gray-500">
                            {formatDate(record.plan_start_date)}<br />
                            <span className="text-xs text-gray-400">to {formatDate(record.plan_term_date) || 'N/A'}</span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-700">
                              {record.wallet_type_code}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500">{record.purse_name}</td>
                          <td className="px-4 py-3 text-sm font-medium text-green-600 text-right">
                            ${parseFloat(record.funding_amount || 0).toFixed(2)}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500">{record.rule_number}</td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getApprovalBadge(record.approval_status)}`}>
                              {record.approval_status}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getApprovalBadge(record.newApprovalStatus)}`}>
                              {record.newApprovalStatus}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            {record.hasUpdate ? (
                              <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-amber-100 text-amber-700">
                                WILL CHANGE
                              </span>
                            ) : (
                              <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-500">
                                NO CHANGE
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {updateTotalPages > 1 && (
                  <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
                    <div className="text-sm text-gray-700">
                      Showing {((updatePage - 1) * pageSize) + 1} to {Math.min(updatePage * pageSize, matchedRecords.length)} of {matchedRecords.length}
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => setUpdatePage(p => Math.max(1, p - 1))} disabled={updatePage === 1}
                        className="p-1 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed">
                        <ChevronLeft className="w-5 h-5" />
                      </button>
                      <span className="text-sm text-gray-700">Page {updatePage} of {updateTotalPages}</span>
                      <button onClick={() => setUpdatePage(p => Math.min(updateTotalPages, p + 1))} disabled={updatePage === updateTotalPages}
                        className="p-1 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed">
                        <ChevronRight className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

          {/* Empty State for Update Tab */}
          {!isProcessingFile && !uploadedFileData && (
            <div className="bg-white rounded-lg border border-gray-200 p-8">
              <div className="text-center py-12">
                <Upload className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Update Funding Status</h3>
                <p className="text-gray-500 max-w-md mx-auto">
                  Select a tenant and run ID, then upload a CSV or Excel file with Member ID and Approval Status columns to update records.
                </p>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PrefundingReport;
